import { useState, useEffect, useMemo, useCallback, memo, useRef } from "react"
import { IconButton, TextField, Box, useTheme, Tooltip, Button, MenuItem } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"
import "dayjs/locale/es"
import { MaterialReactTable, useMaterialReactTable } from "material-react-table"
import { MRT_Localization_ES } from "material-react-table/locales/es"

import { useQuery } from "@tanstack/react-query"
import fetchwrapper from "../../../../services/interceptors/fetchwrapper"
import { Refresh, Visibility, VisibilityOff, SelectAll, Deselect } from "@mui/icons-material"
import CustomBackdrop from "../../../../components/CustomBackdrop"
import Swal from "sweetalert2"

// Configurar dayjs en español
dayjs.locale("es")

// Funciones de conversión de fechas (sin cambios)
const convertToInputDate = (dateStr) => {
  if (!dateStr) return null
  if (dayjs.isDayjs(dateStr)) return dateStr

  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const [day, month, year] = dateStr.split("/")
    return dayjs(`${year}-${month}-${day}`)
  }

  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dayjs(dateStr)
  }

  const parsed = dayjs(dateStr)
  return parsed.isValid() ? parsed : null
}

const convertToDisplayDate = (dateValue) => {
  if (!dateValue) return ""

  try {
    if (dateValue.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [year, month, day] = dateValue.split("/")
      dateValue = `${day}/${month}/${year}`
    }
  } catch (_) {}

  const dayjsDate = dayjs.isDayjs(dateValue) ? dateValue : dayjs(dateValue)
  if (!dayjsDate.isValid()) return ""
  return dayjsDate.format("DD/MM/YYYY")
}

// Componente de filtro debounceado (sin cambios)
const DebouncedFilter = memo(({ column }) => {
  const [filterValue, setFilterValue] = useState(column.getFilterValue() || "")
  const timeoutRef = useRef(null)

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      column.setFilterValue(filterValue || undefined)
    }, 300)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [filterValue, column])

  return (
    <TextField
      variant="standard"
      placeholder="Filtrar"
      value={filterValue}
      onChange={(e) => setFilterValue(e.target.value)}
      size="small"
      fullWidth
    />
  )
})

// Componente de celda editable corregido
const EditableCell = memo(({ editableColumn, currentValue, onFieldChange, originalRowId, columnKey }) => {
  const [localValue, setLocalValue] = useState(currentValue)
  const timeoutRef = useRef(null)
  const isInitialMount = useRef(true)

  // Sincronizar valor local cuando cambie el valor externo
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    setLocalValue(currentValue)
  }, [currentValue])

  // Debounce para cambios de texto y números
  const debouncedHandleChange = useCallback(
    (newValue) => {
      setLocalValue(newValue)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        onFieldChange(originalRowId, columnKey, newValue)
      }, 200)
    },
    [originalRowId, columnKey, onFieldChange],
  )

  // Handle inmediato para fechas y selects
  const immediateHandleChange = useCallback(
    (newValue) => {
      setLocalValue(newValue)
      onFieldChange(originalRowId, columnKey, newValue)
    },
    [originalRowId, columnKey, onFieldChange],
  )

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Renderizar campo editable según el tipo
  switch (editableColumn.type) {
    case "text":
      return (
        <TextField
          value={localValue || ""}
          onChange={(e) => debouncedHandleChange(e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          placeholder={editableColumn.placeholder || ""}
        />
      )
    case "number":
      return (
        <TextField
          type="number"
          value={localValue || ""}
          onChange={(e) => debouncedHandleChange(e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          inputProps={{
            step: editableColumn.step || 0.01,
            min: editableColumn.min,
            max: editableColumn.max,
          }}
        />
      )
    case "select":
      return (
        <TextField
          select
          value={localValue || ""}
          onChange={(e) => immediateHandleChange(e.target.value)}
          size="small"
          variant="standard"
          fullWidth
        >
          {editableColumn.options?.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )
    case "date":
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <DatePicker
            value={convertToInputDate(localValue)}
            onChange={(newValue) => {
              const displayDate = convertToDisplayDate(newValue)
              immediateHandleChange(displayDate)
            }}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                size: "small",
                variant: "standard",
                fullWidth: true,
                placeholder: "dd/mm/yyyy",
              },
            }}
          />
        </LocalizationProvider>
      )
    default:
      return (
        <TextField
          value={localValue || ""}
          onChange={(e) => debouncedHandleChange(e.target.value)}
          size="small"
          variant="standard"
          fullWidth
        />
      )
  }
})

// Toolbar (sin cambios significativos)
const CustomToolbar = memo(
  ({
    refetch,
    allCurrentlySelected,
    handleToggleSelectAll,
    displayDataLength,
    showOnlySelected,
    setShowOnlySelected,
    selectedDataLength,
    theme,
  }) => (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: 1 }}>
      <Tooltip title="Recargar datos">
        <IconButton onClick={refetch} size="small">
          <Refresh />
        </IconButton>
      </Tooltip>

      <Button
        variant={allCurrentlySelected ? "contained" : "outlined"}
        color={allCurrentlySelected ? "secondary" : "primary"}
        size="small"
        startIcon={allCurrentlySelected ? <Deselect /> : <SelectAll />}
        onClick={handleToggleSelectAll}
        disabled={displayDataLength === 0}
        sx={{
          minWidth: 160,
          fontWeight: "medium",
          textTransform: "none",
          borderRadius: 2,
          px: 2,
          py: 0.5,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: theme.shadows[4],
          },
        }}
      >
        {allCurrentlySelected ? "Deseleccionar todos" : "Seleccionar todos"}
      </Button>

      <Button
        variant={showOnlySelected ? "contained" : "outlined"}
        color={showOnlySelected ? "primary" : "info"}
        size="small"
        startIcon={showOnlySelected ? <Visibility /> : <VisibilityOff />}
        onClick={() => setShowOnlySelected((prev) => !prev)}
        disabled={selectedDataLength === 0}
        sx={(theme) => ({
          minWidth: 180,
          fontWeight: "medium",
          textTransform: "none",
          borderRadius: 2,
          px: 2,
          py: 0.5,
          transition: "all 0.2s ease-in-out",
          position: "relative",
          overflow: "visible",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: theme.shadows[4],
          },
          "&:not(:disabled)": {
            "&::after":
              selectedDataLength > 0 && !showOnlySelected
                ? {
                    content: `"${selectedDataLength}"`,
                    position: "absolute",
                    top: -10,
                    right: -10,
                    backgroundColor: theme.palette.error.main,
                    color: theme.palette.error.contrastText,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                    padding: "2px 8px",
                    minWidth: "22px",
                    height: "22px",
                    whiteSpace: "nowrap",
                  }
                : {},
          },
        })}
      >
        {showOnlySelected ? "Mostrar todos" : "Mostrar seleccionados"}
      </Button>
    </Box>
  ),
)

const LargeScreenTable = memo(
  ({
    theme,
    data,
    columnsTable,
    isLoading,
    isError,
    errorMsgFilterSearch,
    refetch,
    selectedRecords,
    editableFields,
    onSelectionChange,
    onFieldChange,
    editableColumns = [],
  }) => {
    const [showOnlySelected, setShowOnlySelected] = useState(false)

    // Crear un Set para búsquedas O(1) de registros seleccionados
    const selectedSet = useMemo(() => {
      return new Set(selectedRecords)
    }, [selectedRecords])

    // Crear datos enriquecidos con IDs únicos basados en el índice original
    const enrichedData = useMemo(() => {
      if (!data || !Array.isArray(data)) return []
      return data.map((item, originalIndex) => ({
        ...item,
        __originalIndex: originalIndex, // ID único basado en posición original
      }))
    }, [data])

    // Datos a mostrar (filtrados o todos)
    const displayData = useMemo(() => {
      if (!showOnlySelected) return enrichedData
      return enrichedData.filter((item) => selectedSet.has(item.__originalIndex))
    }, [enrichedData, showOnlySelected, selectedSet])

    // Auto-desactivar "mostrar solo seleccionados" si no hay seleccionados
    useEffect(() => {
      if (showOnlySelected && selectedRecords.length === 0) {
        setShowOnlySelected(false)
      }
    }, [showOnlySelected, selectedRecords.length])

    // Manejar selección simplificada
    const handleRowSelectionChange = useCallback(
      (updater) => {
        // Obtener selección actual de la tabla (basada en índices de displayData)
        const currentTableSelection = selectedRecords.reduce((acc, originalIndex) => {
          const displayIndex = displayData.findIndex((item) => item.__originalIndex === originalIndex)
          if (displayIndex !== -1) {
            acc[displayIndex] = true
          }
          return acc
        }, {})

        // Aplicar la actualización
        const newTableSelection = typeof updater === "function" ? updater(currentTableSelection) : updater

        // Convertir de vuelta a índices originales
        const newSelectedRecords = Object.keys(newTableSelection)
          .filter((displayIndex) => newTableSelection[displayIndex])
          .map((displayIndex) => {
            const item = displayData[parseInt(displayIndex)]
            return item?.__originalIndex
          })
          .filter((originalIndex) => originalIndex !== undefined)

        onSelectionChange(newSelectedRecords)
      },
      [displayData, selectedRecords, onSelectionChange],
    )

    // Mapear selección para la tabla (índices de displayData)
    const rowSelection = useMemo(() => {
      return displayData.reduce((acc, item, displayIndex) => {
        if (selectedSet.has(item.__originalIndex)) {
          acc[displayIndex] = true
        }
        return acc
      }, {})
    }, [displayData, selectedSet])

    // Manejar seleccionar/deseleccionar todos
    const handleToggleSelectAll = useCallback(() => {
      const visibleOriginalIndices = displayData.map((item) => item.__originalIndex)
      const allVisibleSelected = visibleOriginalIndices.every((index) => selectedSet.has(index))

      if (allVisibleSelected) {
        // Deseleccionar todos los visibles
        const newSelected = selectedRecords.filter((index) => !visibleOriginalIndices.includes(index))
        onSelectionChange(newSelected)
      } else {
        // Seleccionar todos los visibles
        const newSelected = [...new Set([...selectedRecords, ...visibleOriginalIndices])]
        onSelectionChange(newSelected)
      }
    }, [displayData, selectedRecords, selectedSet, onSelectionChange])

    // Verificar si todos los visibles están seleccionados
    const allCurrentlySelected = useMemo(() => {
      if (displayData.length === 0) return false
      const visibleOriginalIndices = displayData.map((item) => item.__originalIndex)
      return visibleOriginalIndices.length > 0 && visibleOriginalIndices.every((index) => selectedSet.has(index))
    }, [displayData, selectedSet])

    // Columnas optimizadas
    const columns = useMemo(
      () =>
        columnsTable.map((col) => {
          const editableColumn = editableColumns.find((ec) => ec.accessorKey === col.accessorKey)

          if (editableColumn) {
            return {
              ...col,
              enableColumnFilter: true,
              Filter: ({ column }) => <DebouncedFilter column={column} />,
              Cell: ({ row }) => {
                const originalRowId = row.original.__originalIndex

                // Verificar que el originalRowId sea válido
                if (originalRowId === undefined) {
                  return <span>Error: ID no válido</span>
                }

                const currentValue =
                  editableFields[originalRowId]?.[col.accessorKey] ??
                  row.original[col.accessorKey] ??
                  editableColumn.defaultValue ??
                  ""

                return (
                  <EditableCell
                    editableColumn={editableColumn}
                    currentValue={currentValue}
                    onFieldChange={onFieldChange}
                    originalRowId={originalRowId}
                    columnKey={col.accessorKey}
                  />
                )
              },
            }
          }

          return {
            ...col,
            enableColumnFilter: true,
            Filter: ({ column }) => <DebouncedFilter column={column} />,
          }
        }),
      [columnsTable, editableColumns, editableFields, onFieldChange],
    )

    const table = useMaterialReactTable({
      columns,
      data: displayData,

      // Virtualización mejorada
      enableRowVirtualization: true,
      rowVirtualizerOptions: {
        overscan: 5,
        estimateSize: () => 52,
      },

      // Configuración de selección
      enableRowSelection: true,
      enableMultiRowSelection: true,
      enableSelectAll: false,
      onRowSelectionChange: handleRowSelectionChange,
      getRowId: useCallback((row, index) => {
        // Usar el __originalIndex si existe, sino usar el índice como fallback
        return row.__originalIndex !== undefined ? row.__originalIndex.toString() : index.toString()
      }, []),

      // Estado de la tabla
      state: {
        isLoading,
        rowSelection,
      },

      // Configuración inicial
      initialState: {
        showColumnFilters: true,
        density: "compact",
        pagination: {
          pageSize: 100,
          pageIndex: 0,
        },
      },

      // Localización y estilos
      localization: MRT_Localization_ES,
      muiToolbarAlertBannerProps: isError ? { color: "error", children: errorMsgFilterSearch } : undefined,

      muiTableContainerProps: {
        sx: {
          maxHeight: "70vh",
          overflow: "auto",
        },
      },

      muiTableBodyRowProps: ({ row }) => ({
        sx: {
          backgroundColor: row.getIsSelected()
            ? theme.palette.action.selected
            : row.index % 2 === 0
              ? "#f5f5f5"
              : "#fff",
        },
      }),

      renderTopToolbarCustomActions: () => (
        <CustomToolbar
          refetch={refetch}
          allCurrentlySelected={allCurrentlySelected}
          handleToggleSelectAll={handleToggleSelectAll}
          displayDataLength={displayData.length}
          showOnlySelected={showOnlySelected}
          setShowOnlySelected={setShowOnlySelected}
          selectedDataLength={selectedRecords.length}
          theme={theme}
        />
      ),
    })

    return <MaterialReactTable table={table} />
  },
)

const CustomTable = memo((props) => {
  const theme = useTheme()
  return (
    <div>
      <LargeScreenTable theme={theme} {...props} />
    </div>
  )
})

const CustomMultiRowSelectionNoAprobado = ({
  endpoint = "",
  endpointJson = {},
  errorMsgFilterSearch = "Error en cargar datos",
  queryKeyModal = "",
  perPage = 10,
  columnsTable = [],
  editableColumns = [],
  onHandleSelectedData,
}) => {
  const { data, isLoading, isError, refetch, isFetching } = useGetData()

  // Estados simplificados
  const [selectedRecords, setSelectedRecords] = useState([]) // Array de índices originales
  const [editableFields, setEditableFields] = useState({}) // { originalIndex: { columnKey: value } }

  // Inicialización optimizada
  useEffect(() => {
    if (data && data.length > 0) {
      setEditableFields((prev) => {
        const newFields = { ...prev }

        data.forEach((item, originalIndex) => {
          if (!newFields[originalIndex]) {
            newFields[originalIndex] = {}
            editableColumns.forEach((column) => {
              const { accessorKey, defaultValue = "", type } = column
              let initialValue = item[accessorKey] || defaultValue

              if (type === "date" && initialValue) {
                initialValue = convertToDisplayDate(initialValue)
              }

              newFields[originalIndex][accessorKey] = initialValue
            })
          }
        })

        return newFields
      })

      // Limpiar selecciones que excedan el tamaño de los datos
      setSelectedRecords((prev) => prev.filter((index) => index < data.length))
    }
  }, [data, editableColumns])

  useEffect(() => {
    refetch()
  }, [refetch])

  // Callbacks optimizados
  const handleSelectionChange = useCallback((newSelectedRecords) => {
    setSelectedRecords(newSelectedRecords)
  }, [])

  const handleFieldChange = useCallback((originalRowIndex, columnKey, newValue) => {
    setEditableFields((prev) => ({
      ...prev,
      [originalRowIndex]: {
        ...prev[originalRowIndex],
        [columnKey]: newValue,
      },
    }))
  }, [])

  function useGetData() {
    return useQuery({
      queryKey: [queryKeyModal],
      queryFn: async () => {
        try {
          const response = await fetchwrapper(`${endpoint}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ...endpointJson,
            }),
          })
          const result = await response.json()
          return result.data
        } catch (errorResponse) {
          Swal.fire({
            title: "Error",
            text: errorResponse?.details?.msg || "Error en la petición datos",
            icon: "error",
            confirmButtonText: "OK",
          })
          throw new Error(errorResponse?.details?.msg || "Error en la petición datos")
        }
      },
      keepPreviousData: false,
      retry: 1,
    })
  }

  const handleProcessAllRecords = useCallback(() => {
    if (data && data.length > 0) {
      // Construir el formato esperado por la función original
      const processedRecords = data.map((item, originalIndex) => ({
        originalData: { ...item },
        isSelected: selectedRecords.includes(originalIndex),
        editableFields: editableFields[originalIndex] || {},
      }))

      onHandleSelectedData(processedRecords)
    }
  }, [data, selectedRecords, editableFields, onHandleSelectedData])

  return (
    <Box>
      <CustomBackdrop isLoading={isLoading || isFetching} />
      <div style={{ textAlign: "center", marginBottom: "15px" }}>
        <Button variant="contained" color="primary" onClick={handleProcessAllRecords}>
          Aprobar Pago
        </Button>

        <div style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}>
          Total: {data?.length || 0} | Seleccionados: {selectedRecords.length}
        </div>
      </div>

      <CustomTable
        data={isError ? [] : data || []}
        columnsTable={columnsTable || []}
        perPage={perPage}
        isLoading={isLoading}
        isError={isError}
        errorMsgFilterSearch={errorMsgFilterSearch}
        refetch={refetch}
        selectedRecords={selectedRecords}
        editableFields={editableFields}
        onSelectionChange={handleSelectionChange}
        onFieldChange={handleFieldChange}
        editableColumns={editableColumns}
      />
    </Box>
  )
}

export default CustomMultiRowSelectionNoAprobado
